// FRONT-820: the composer's Fast/Thoughtful quick-switch, scoped to one
// conversation (chat_sessions.model_mode) instead of a single browser-wide
// localStorage key that bled across every open chat.
//
// The optimistic write in `usePickChatModelMode` populates the query cache
// synchronously, before the PUT round-trips — `use-agent-chat.ts` reads this
// same cache entry at send time via `ensureChatModelMode` (not a bare
// `getQueryData`, which could read an empty cache slot on the very first send
// after a reload, before this hook's own GET has resolved — a real race a
// pr-panel review caught before merge). Any component reading this query (the
// composer pill, the header chip) re-renders off the same cache entry, so
// they can never show two different values for one session.
//
// FRONT-1455: `usePickChatModelMode` is the only mutation an already-open
// chat's pill uses — it writes the per-session mode AND mirrors the pick
// into the user's standing default (`useSetDefaultChatModelMode`'s table)
// in one round trip, so a click always updates both "this chat" and "my
// usual pick going forward". `useSetDefaultChatModelMode` on its own remains
// only for the classic pre-session composer, which has no session to also
// write.

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type MutationOptions,
} from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import type { ChatModelMode } from '~/lib/chat-model-mode';
import { toast } from 'sonner';

interface ChatModelModeResponse {
  modelMode: ChatModelMode | null;
}

export const chatModelModeKeys = {
  bySessionId: (sessionId: string) => ['llm', 'chat-mode', sessionId] as const,
};

const STALE_TIME_MS = 30_000;

/**
 * Returns `null` quietly on BOTH 404 and 403 (FRONT-1412) — keep it that way.
 * A model-preference lookup must never be able to block sending a message:
 * `ensureChatModelMode` is awaited from inside the AI SDK's transport `body`
 * callback, which resolves strictly before the fetch is issued, so throwing
 * here means the `/stream` POST never fires at all.
 *
 * FRONT-1398 removed the reason those statuses used to fire on the happy
 * path. The route now resolves the EFFECTIVE mode for a chat that hasn't
 * sent its first message yet — falling back to the caller's standing default
 * for a session with no `chat_sessions` row at all, or one that exists but
 * is still unowned (created by `touchSession` when `useSSE()` opens the
 * events stream ahead of the first message) — so the pre-session composer's
 * pill now reflects the real, live default instead of resetting to neutral.
 * Ownership is no longer part of that decision: the route 403s only a row
 * genuinely owned by a DIFFERENT user, which in practice needs two people to
 * collide on a deterministic session id (see `inbox-doc-<filename>`).
 *
 * So both branches below are now defensive safety nets rather than everyday
 * states — but they must stay, for the FRONT-1412 reason above: a rare 403
 * from a genuine ownership collision still must not take the user's message
 * down with it. Do not narrow this back to 404-only.
 */
export async function fetchChatModelMode(sessionId: string): Promise<ChatModelMode | null> {
  try {
    const res = await api.get<ChatModelModeResponse>(
      `/api/llm/chat-mode/${encodeURIComponent(sessionId)}`,
    );
    return res.modelMode;
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === 404 || status === 403) return null;
    throw err;
  }
}

export function useChatModelMode(sessionId: string | undefined) {
  return useQuery<ChatModelMode | null>({
    queryKey: chatModelModeKeys.bySessionId(sessionId ?? ''),
    queryFn: () => fetchChatModelMode(sessionId!),
    enabled: !!sessionId,
    staleTime: STALE_TIME_MS,
  });
}

/**
 * Send-time read for `use-agent-chat.ts`'s transport body. Deliberately NOT a
 * bare `queryClient.getQueryData` — on the very first send after a reload (or
 * right after the inbox modal mounts), this hook's own GET may not have
 * resolved yet, and a plain cache read would silently see an empty slot and
 * drop a persisted mode for that turn. `ensureQueryData` returns the cached
 * value immediately once it exists (the common case — the pill's optimistic
 * write, or a settled GET, already populated it) and otherwise awaits the
 * same fetch this hook uses, so the transport can never race it.
 */
export function ensureChatModelMode(
  queryClient: QueryClient,
  sessionId: string,
): Promise<ChatModelMode | null> {
  return queryClient.ensureQueryData({
    queryKey: chatModelModeKeys.bySessionId(sessionId),
    queryFn: () => fetchChatModelMode(sessionId),
    staleTime: STALE_TIME_MS,
  });
}

/**
 * FRONT-1035: the user's standing Fast/Thoughtful default, applied to new
 * chats. Distinct from the per-session pair above — this always resolves
 * (no per-resource ownership check, no 404-on-missing-row case; a user with
 * no saved default just reads back `null`), so it's a plain query/mutation
 * pair with no optimistic-cache races to guard against. The pre-session
 * composer (no `sessionId` yet) reads/writes this instead of a session mode.
 */
const DEFAULT_CHAT_MODE_KEY = ['llm', 'chat-mode', 'default'] as const;

async function fetchDefaultChatModelMode(): Promise<ChatModelMode | null> {
  const res = await api.get<ChatModelModeResponse>('/api/llm/chat-mode/default');
  return res.modelMode;
}

/**
 * `enabled` defaults to true but should be passed `!sessionId` by callers
 * that ALSO hold a per-session query — e.g. `chat-input.tsx` calls both
 * hooks unconditionally (rules of hooks) but only ever reads one, so the
 * irrelevant one should never fire a real request.
 */
export function useDefaultChatModelMode(enabled = true) {
  return useQuery<ChatModelMode | null>({
    queryKey: DEFAULT_CHAT_MODE_KEY,
    queryFn: fetchDefaultChatModelMode,
    staleTime: STALE_TIME_MS,
    enabled,
  });
}

export function useSetDefaultChatModelMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mode: ChatModelMode | null) => {
      const res = await api.put<ChatModelModeResponse>('/api/llm/chat-mode/default', {
        modelMode: mode,
      });
      return res.modelMode;
    },
    onMutate: async (mode: ChatModelMode | null) => {
      await qc.cancelQueries({ queryKey: DEFAULT_CHAT_MODE_KEY });
      const previous = qc.getQueryData<ChatModelMode | null>(DEFAULT_CHAT_MODE_KEY);
      qc.setQueryData(DEFAULT_CHAT_MODE_KEY, mode);
      return { previous };
    },
    onError: (_err, _mode, context) => {
      qc.setQueryData(DEFAULT_CHAT_MODE_KEY, context?.previous ?? null);
      toast.error("Couldn't update your default mode. Try again.");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: DEFAULT_CHAT_MODE_KEY });
    },
  });
}

/**
 * FRONT-1412 review finding: the toast message for a failed mode-set. 403
 * and 404 are the SAME underlying situation here — an unowned or
 * not-yet-existing `chat_sessions` row (see `fetchChatModelMode`'s docblock
 * above, which already treats them identically on the read side) — so they
 * must share a message rather than 403 falling through to a generic one.
 * Exported (pure) so it's unit-testable without mounting the mutation.
 */
export function setChatModelModeErrorMessage(status: number | undefined): string {
  return status === 404 || status === 403
    ? "Couldn't set your mode yet — send a message first, then try again."
    : "Couldn't update your mode. Try again.";
}

/** Optimistic-update rollback context for {@link pickChatModelModeOptions}. */
interface PickChatModelModeContext {
  previousSession: ChatModelMode | null | undefined;
  previousDefault: ChatModelMode | null | undefined;
}

/**
 * FRONT-1455 finding 1: thrown by `pickChatModelModeOptions`'s `mutationFn`
 * instead of the raw API error, so `onError` can tell WHICH of the two PUTs
 * actually reached the server without relying on any module-level mutable
 * state (retry-safe: TanStack Query re-runs `mutationFn` from the top, and
 * each retry throws its own fresh instance of this). `sessionWriteSucceeded`
 * is the one fact `onError` needs: false means neither write landed (or the
 * session write itself is what failed), true means the session write is
 * already real on the server and must NOT be rolled back — only the default
 * mirror failed. `cause` carries the original API error so status-based
 * decisions (the retry predicate, the toast copy) still work unchanged.
 */
export class ChatModelModePickError extends Error {
  constructor(
    public readonly sessionWriteSucceeded: boolean,
    public readonly cause: unknown,
  ) {
    super('Failed to update chat model mode');
    this.name = 'ChatModelModePickError';
  }
}

/** Unwraps a {@link ChatModelModePickError} back to the underlying API
 *  error's `status`, so the retry predicate and the toast copy don't need
 *  to know about the wrapper. Falls back to reading `status` off the raw
 *  error directly, defensively, in case something other than `mutationFn`
 *  below ever surfaces here. */
function pickFailureStatus(err: unknown): number | undefined {
  const cause = err instanceof ChatModelModePickError ? err.cause : err;
  return (cause as { status?: number } | undefined)?.status;
}

/**
 * FRONT-1455: the mutation options behind `usePickChatModelMode` — the ONE
 * mutation both pill surfaces (`chat-input.tsx`'s session-scoped branch,
 * `pwa-chat-mode.tsx`) call when the user picks (or clears) a Fast/
 * Thoughtful mode from an already-open chat. The bug this fixes: only
 * `useSetDefaultChatModelMode` ever wrote the user's STANDING preference
 * (`user_settings.default_chat_model_mode`), and it was wired only into the
 * pre-session composer — which the top-bar chat design (the default since
 * FRONT-1429) never renders, and the phone composer never had a pre-session
 * branch at all. So the standing column stayed empty forever and every
 * brand-new chat opened neutral, reading as "my pick reset itself".
 *
 * A click here writes BOTH: the per-conversation mode (unchanged behaviour,
 * `chat_sessions.model_mode` via `PUT /api/llm/chat-mode/:externalId`) AND
 * the user's standing default (`PUT /api/llm/chat-mode/default`) — clicked
 * mode becomes "what I use going forward", not just "what this one chat
 * uses". This does NOT weaken BUG-043's invariant: a chat that's already
 * open and explicitly set keeps its own per-session value regardless of
 * what the standing default does later — only the moment of THIS click also
 * updates the default, mirroring what the user just did.
 *
 * The two PUTs are SEQUENTIAL — the per-session write goes first, and the
 * default write is only issued once it has actually resolved. This is
 * load-bearing, not stylistic: a document chat's session id
 * (`inbox-doc-<filename>`) is unique only PER USER (see chat-mode-store.ts's
 * module doc comment), so a colleague who already owns that row makes the
 * per-session PUT refuse outright (403, no retry — see the `retry` predicate
 * below). Firing both writes in parallel (the previous `Promise.all` shape)
 * meant that refusal raced the default write, which had no reason to fail
 * and would land anyway — so a click the user was TOLD failed would still
 * silently overwrite their standing preference for every future chat.
 * Sequencing closes that: a refused session write means the default PUT is
 * never issued at all.
 *
 * `onError` mirrors that asymmetry rather than reverting both cache entries
 * unconditionally. It reads `ChatModelModePickError.sessionWriteSucceeded`
 * (thrown by `mutationFn`, never inferred from outside state) to decide: if
 * the session write is what failed, both entries revert to their pre-click
 * values — nothing landed. If the session write succeeded and only the
 * default mirror failed, the session cache entry is left exactly as the
 * optimistic write set it — that value is now real on the server, so the
 * pill correctly stays lit — and only the default entry reverts.
 * `onSettled`'s invalidation cannot be relied on to paper over either case:
 * the desktop switches the standing-default query off while a
 * session-scoped composer is mutating (`chat-input.tsx`'s
 * `useDefaultChatModelMode(!sessionId)`), and the phone composer never
 * mounts that query at all — so "invalidate and let the next fetch fix it"
 * would just leave a stale cache entry marked dirty with nothing around to
 * refetch it.
 *
 * Retries re-run `mutationFn` as a whole (both PUTs, from the top) — safe,
 * since both are idempotent upserts (keyed by `externalId` / by `userId`
 * respectively), so replaying an already-succeeded session write ahead of a
 * retried default write is harmless.
 *
 * Pulled out as its own function (rather than inlined in the hook) so a
 * test can build the exact same options against a plain `QueryClient` and
 * drive them through `@tanstack/react-query`'s re-exported `MutationObserver`
 * directly — `useMutation` is itself a thin wrapper over `MutationObserver`
 * (see `@tanstack/react-query`'s own `useMutation.ts`: `new
 * MutationObserver(client, options)`), so this is the real mutation logic
 * under test, not a re-implementation. This repo's frontend suite has no
 * DOM/jsdom, which rules out rendering the hook itself.
 */
export function pickChatModelModeOptions(
  qc: QueryClient,
  sessionId: string | undefined,
): MutationOptions<ChatModelMode | null, unknown, ChatModelMode | null, PickChatModelModeContext> {
  const sessionKey = chatModelModeKeys.bySessionId(sessionId ?? '');
  return {
    mutationFn: async (mode: ChatModelMode | null) => {
      let sessionRes: ChatModelModeResponse;
      try {
        sessionRes = await api.put<ChatModelModeResponse>(
          `/api/llm/chat-mode/${encodeURIComponent(sessionId!)}`,
          { modelMode: mode },
        );
      } catch (err) {
        // The session write itself failed — the default PUT must never
        // fire, so a refused click can't still change the standing pick.
        throw new ChatModelModePickError(false, err);
      }
      try {
        await api.put<ChatModelModeResponse>('/api/llm/chat-mode/default', { modelMode: mode });
      } catch (err) {
        // The session write landed; only the default mirror didn't. Carry
        // that so `onError` rolls back ONLY the default cache entry.
        throw new ChatModelModePickError(true, err);
      }
      return sessionRes.modelMode;
    },
    // FRONT-1398: PUT no longer 404s in normal operation —
    // `authorizeChatModeAccess` (llm-models-routes.ts) answers 401/403/200
    // only, so setting a mode on a not-yet-started chat now succeeds instead
    // of dead-ending. 403 is the real "don't bother retrying" case today
    // (a row claimed by a different org, or owned by a different user). The
    // 404 branch stays only as a cheap defensive safety net for a genuinely
    // unexpected response, not because it's expected to fire.
    retry: (failureCount, error) => {
      const status = pickFailureStatus(error);
      if (status === 403 || status === 404) return false;
      return failureCount < 3;
    },
    onMutate: async (mode: ChatModelMode | null) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: sessionKey }),
        qc.cancelQueries({ queryKey: DEFAULT_CHAT_MODE_KEY }),
      ]);
      const previousSession = qc.getQueryData<ChatModelMode | null>(sessionKey);
      const previousDefault = qc.getQueryData<ChatModelMode | null>(DEFAULT_CHAT_MODE_KEY);
      qc.setQueryData(sessionKey, mode);
      qc.setQueryData(DEFAULT_CHAT_MODE_KEY, mode);
      return { previousSession, previousDefault };
    },
    onError: (err, _mode, context) => {
      // Only roll back the session cache entry if the session write is what
      // actually failed — if it succeeded, that value is real on the
      // server and must stay lit (see the docblock above).
      const sessionWriteSucceeded =
        err instanceof ChatModelModePickError && err.sessionWriteSucceeded;
      if (!sessionWriteSucceeded) {
        qc.setQueryData(sessionKey, context?.previousSession ?? null);
      }
      qc.setQueryData(DEFAULT_CHAT_MODE_KEY, context?.previousDefault ?? null);
      // FRONT-1412's shared message helper is kept as-is. FRONT-1398 makes
      // both statuses it distinguishes near-unreachable (a write to a
      // not-yet-started chat now succeeds instead of 404ing, and an unowned
      // row no longer 403s), so this is a fallback path either way.
      const status = pickFailureStatus(err);
      toast.error(setChatModelModeErrorMessage(status));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: sessionKey });
      qc.invalidateQueries({ queryKey: DEFAULT_CHAT_MODE_KEY });
    },
  };
}

export function usePickChatModelMode(sessionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation(pickChatModelModeOptions(qc, sessionId));
}
